import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  ArtifactRow,
  EditorialArtifactService,
  WorkbenchState,
} from '../../services/editorial-artifact-service';
import { AdminService } from '../../services/admin-service';

/**
 * Read-only Editorial Workbench (Pass A3).
 *
 * Enter a registration and inspect its full persisted editorial state end-to-end
 * — pack, brief, draft, verification, evidence (status + canonical rights),
 * media, timeline, warnings, version history — WITHOUT touching JSON.
 *
 * READ-ONLY on load: only the Stage A1 GET artifact routes + GET intelligence/{reg}
 * are called. No LLM, no pipeline, no cost, no automatic POST. The only generation
 * is an explicit "Generate (live, paid)" button, which POSTs then re-fetches via GET.
 *
 * "Rendered readably" = legible plain structure so the draft can be read and judged.
 * NOT house-style, NOT magazine layout, NOT typography. Ugly-but-clear is correct.
 */
@Component({
  selector: 'pla-admin-editorial-workbench',
  templateUrl: './admin-editorial-workbench.component.html',
  styleUrls: ['./admin-editorial-workbench.component.scss'],
})
export class AdminEditorialWorkbenchComponent implements OnInit {
  regInput = '';
  reg = '';
  loading = false;
  state: WorkbenchState | null = null;

  // explicit generation state (button-only)
  generating: '' | 'packbrief' | 'draft' = '';
  genMessage = '';

  // inject-source (Human Injection, Type 2) — explicit live action
  injMode: 'guided' | 'manual' = 'guided';   // natural-language is the primary path
  injInstruction = '';           // plain-English note (guided mode)
  injUrl = '';
  injSupports = '';              // claim id (manual fallback)
  injConfidence: number | null = null;
  injecting = false;
  injMessage = '';
  lastRoute = '';                // 'corroborate' | 'direct_research' | 'note' (guided)
  justPromoted: string[] = [];   // claim ids promoted by the most recent injection
  justInjectedId = '';           // evidence id of the most recent injected source

  // Derived view lists — computed ONCE per load (NOT getters), so *ngFor never
  // re-allocates arrays or re-renders on every change-detection cycle. Getters
  // returning new arrays here made the page unresponsive inside the busy host SPA.
  evidenceNonMedia: any[] = [];
  mediaItems: any[] = [];
  timeline: any[] = [];
  warnings: string[] = [];
  claimOptions: any[] = [];
  notebook: any[] = [];          // Route 2/3 notes & directions — NEVER evidence
  comparables: any[] = [];       // verified comparable sales (from the dossier), price-sorted
  comparablesStats: { count: number; min: number; max: number; median: number } | null = null;

  // "Research this plate" — full pipeline with visible staged progress.
  researching = false;
  researchMsg = '';
  stages: { key: string; label: string; status: 'pending' | 'active' | 'done' | 'failed' }[] = [];

  // Copy-out + paste-back (the finishing loop).
  copyMsg = '';
  pasteText = '';
  savingEdit = false;
  editMsg = '';

  // media thumbnails that failed to load (broken URL / hotlink-blocked) → placeholder
  brokenImg = new Set<string>();

  // Add media (editor insertion) — paste a link OR upload/paste an image; attribute it.
  addUrl = '';                 // pasted image/media URL
  addFile: File | null = null; // chosen/pasted image file
  addPreview = '';             // object-URL preview of the chosen file
  addCaption = '';
  addCredit = '';              // attribution name / @handle
  addCreditUrl = '';           // attribution link (instagram / website)
  addType: 'image' | 'video' = 'image';
  addingMedia = false;
  addMsg = '';

  readonly examples = ['F1', 'JB1', 'FU2'];

  constructor(
    private svc: EditorialArtifactService,
    private admin: AdminService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const param = this.route.snapshot.paramMap.get('registration');
    if (param) {
      this.regInput = param;
      this.inspect(); // route param => read-only load (GETs only), no generation
    }
  }

  /** READ-ONLY: fetch all persisted artifacts. Never POSTs. */
  inspect(): void {
    const reg = this.svc.normalize(this.regInput);
    if (!reg) {
      return;
    }
    this.reg = reg;
    this.loading = true;
    this.genMessage = '';
    // keep the URL shareable/refreshable — refresh re-runs GETs only, no cost
    this.router.navigate(['/admin/editorial-workbench', reg], { replaceUrl: true });
    this.svc.loadAll(reg).subscribe((s) => {
      this.applyState(s);
      this.loading = false;
    });
  }

  pick(reg: string): void {
    this.regInput = reg;
    this.inspect();
  }

  private reload(): void {
    this.svc.loadAll(this.reg).subscribe((s) => {
      this.applyState(s);
      this.generating = '';
    });
  }

  // ---- Step 1: Research this plate — full pipeline with visible stage progress -- #

  private setStage(key: string, status: 'pending' | 'active' | 'done' | 'failed'): void {
    const s = this.stages.find((x) => x.key === key);
    if (s) {
      s.status = status;
    }
  }

  /** Poll a GET until `pred` holds (or tries run out). Handles the API-gateway 30s
   * cap: a paid POST may return 503 while the Lambda keeps working and persists a
   * few seconds later, so we watch for the artifact to appear rather than trusting
   * the POST's own response. */
  private async pollUntil(get: () => Promise<any>, pred: (v: any) => boolean, tries: number, delayMs: number): Promise<boolean> {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        if (pred(await get())) {
          return true;
        }
      } catch {
        /* keep polling */
      }
    }
    return false;
  }

  /** PRIMARY: enter a plate → run the real Dev2 pipeline end to end, showing each
   * stage complete. build-dossier (verified spine, read-only from prod) → editorial-
   * brief (web enrich + two-tier pack) → generate-draft (Opus) + audit. */
  async researchPlate(): Promise<void> {
    const reg = this.svc.normalize(this.regInput);
    if (!reg || this.researching) {
      return;
    }
    this.reg = reg;
    this.researching = true;
    this.researchMsg = '';
    this.genMessage = '';
    this.router.navigate(['/admin/editorial-workbench', reg], { replaceUrl: true });
    this.stages = [
      { key: 'spine', label: 'Pulling verified sales (prod, read-only)', status: 'pending' },
      { key: 'web', label: 'Gathering web + media', status: 'pending' },
      { key: 'pack', label: 'Building pack', status: 'pending' },
      { key: 'draft', label: 'Writing draft (Opus)', status: 'pending' },
      { key: 'audit', label: 'Auditing', status: 'pending' },
    ];
    try {
      const before = await firstValueFrom(this.svc.getDraft(reg));
      const beforeVer = (before && before.version) || 0;

      // Stage 1 — verified spine from prod (read-only) -> dossier in dev.
      this.setStage('spine', 'active');
      await firstValueFrom(this.svc.buildDossier(reg, false));
      const dossierOk = await this.pollUntil(() => firstValueFrom(this.svc.getDossier(reg)), (d) => !!d, 8, 4000);
      this.setStage('spine', dossierOk ? 'done' : 'failed');
      if (!dossierOk) {
        this.researchMsg = 'Could not build a dossier for this plate (no prod data or unreachable). Nothing persisted.';
        return;
      }

      // Stages 2 & 3 — web enrichment + two-tier pack (one call).
      this.setStage('web', 'active');
      this.setStage('pack', 'active');
      await firstValueFrom(this.svc.generatePackAndBrief(reg));
      const packOk = await this.pollUntil(() => firstValueFrom(this.svc.getEnrichedPack(reg)), (p) => !!p, 12, 5000);
      this.setStage('web', packOk ? 'done' : 'failed');
      this.setStage('pack', packOk ? 'done' : 'failed');
      if (!packOk) {
        this.researchMsg = 'The pack did not build in time. You can retry with “Generate pack + brief”.';
        return;
      }

      // Stages 4 & 5 — writer (Opus) + post-draft audit. generate-draft 503s at the
      // gateway ~30s then persists; watch for a new draft version to appear.
      this.setStage('draft', 'active');
      this.setStage('audit', 'active');
      await firstValueFrom(this.svc.generateDraft(reg));
      const draftOk = await this.pollUntil(
        () => firstValueFrom(this.svc.getDraft(reg)),
        (d) => !!d && ((d.version || 0) > beforeVer),
        14,
        6000,
      );
      this.setStage('draft', draftOk ? 'done' : 'failed');
      this.setStage('audit', draftOk ? 'done' : 'failed');
      this.researchMsg = draftOk
        ? 'Done — draft ready below.'
        : 'The writer is taking longer than expected; use “Generate draft” to check again shortly.';
    } catch (e: any) {
      this.researchMsg = 'Research failed: ' + (e?.message || e);
    } finally {
      this.researching = false;
      this.svc.loadAll(reg).subscribe((s) => this.applyState(s));
    }
  }

  // ---- Step 4/5: copy the article out, paste the improved version back --------- #

  /** Plain, cleanly-formatted article text (markdown) for copy-out. Handles both a
   * generated structured draft and an editor-pasted (editor_edited) draft. */
  articleText(): string {
    const p = this.draft;
    if (!p) {
      return '';
    }
    if (p.editor_edited && p.body_markdown) {
      return p.body_markdown;
    }
    const out: string[] = [];
    if (p.headline) {
      out.push('# ' + p.headline);
    }
    if (p.standfirst) {
      out.push('', '*' + p.standfirst + '*');
    }
    (p.sections || []).forEach((s: any) => {
      if (s.heading) {
        out.push('', '## ' + s.heading);
      }
      (s.blocks || []).forEach((b: any) => {
        if (b.type === 'pull_quote') {
          out.push('', '> ' + b.text);
        } else if (b.type === 'heading') {
          out.push('', '### ' + b.text);
        } else {
          out.push('', b.text);
        }
      });
    });
    if (p.provenance_line) {
      out.push('', '---', '*' + p.provenance_line + '*');
    }
    return out.join('\n').trim();
  }

  copyArticle(): void {
    const text = this.articleText();
    if (!text) {
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => {
        this.copyMsg = 'Copied ✓';
        setTimeout(() => (this.copyMsg = ''), 2500);
      },
      () => {
        this.copyMsg = 'Copy blocked — select the text and copy manually.';
      },
    );
  }

  /** Step 5: save the pasted, worked-up article as a NEW draft version. */
  savePastedDraft(): void {
    const md = (this.pasteText || '').trim();
    if (!md || this.savingEdit) {
      return;
    }
    this.savingEdit = true;
    this.editMsg = 'Saving your version…';
    this.svc.saveEditedDraft(this.reg, md).subscribe((r) => {
      this.savingEdit = false;
      if (r?.error) {
        this.editMsg = 'Failed: ' + (r.detail || '');
        return;
      }
      this.editMsg = `Saved as draft v${r.new_version} — now the current draft.`;
      this.pasteText = '';
      this.svc.loadAll(this.reg).subscribe((s) => this.applyState(s));
    });
  }

  /** Assign state AND precompute the derived view lists (once, not per CD cycle). */
  private applyState(s: WorkbenchState | null): void {
    this.state = s;
    const ev = (this.pack?.evidence as any[]) || [];
    this.evidenceNonMedia = ev.filter((e) => e.type !== 'media');
    this.mediaItems = ev.filter((e) => e.type === 'media');
    this.claimOptions = ev.filter((e) => e.type === 'claim');
    this.brokenImg.clear(); // reset per-load image-load failures
    this.notebook = ((this.pack?.notebook as any[]) || []).slice().reverse(); // newest first
    this.timeline = (this.dossier?.timeline as any[]) || [];
    this.comparables = this.parseComparables();
    this.warnings = this.buildWarnings();
  }

  /** Parse the dossier's comparable sales into a price-sorted list (+ range/median),
   * so the editor can eyeball the actual comps and spot bad ones (e.g. a £915
   * historical-DVLA row that shouldn't sit in a high-value cohort). */
  private parseComparables(): any[] {
    const raw = (this.dossier?.comparables as any[]) || [];
    const rows = raw.map((c) => {
      const lk = String(c.lastKnownResult || '');
      const pm = lk.replace(/,/g, '').match(/£?\s*(\d+)/);
      const dm = lk.match(/(\d{4}-\d{2}-\d{2})/);
      return {
        registration: c.registration,
        price: pm ? parseInt(pm[1], 10) : null,
        soldDate: dm ? dm[1] : null,
        venue: String(c.note || '').replace(/^via\s+/i, ''),
      };
    });
    rows.sort((a, b) => (b.price || 0) - (a.price || 0));
    const prices = rows.map((r) => r.price).filter((p): p is number => p != null).sort((a, b) => a - b);
    this.comparablesStats = prices.length
      ? { count: rows.length, min: prices[0], max: prices[prices.length - 1], median: prices[Math.floor(prices.length / 2)] }
      : null;
    return rows;
  }

  /** Format a whole-pound figure as £-grouped. */
  money(n: number | null): string {
    return n == null ? '—' : '£' + n.toLocaleString('en-GB');
  }

  /** Flag implausibly-low comps so the editor's eye goes straight to them. */
  isLowComp(price: number | null): boolean {
    return price != null && price < 10000;
  }

  private buildWarnings(): string[] {
    const out: string[] = [];
    const b = this.brief;
    if (b) {
      (b.conflicts || []).forEach((c: any) => out.push(`Conflict [${c.kind}]: ${c.detail}`));
      ((b.single_source && b.single_source.notes) || []).forEach((n: string) => out.push(`Single-source: ${n}`));
      ((b.rights_media_risks && b.rights_media_risks.notes) || []).forEach((n: string) => out.push(`Rights: ${n}`));
    }
    if (this.verification && this.verification.caveat) {
      out.push(`Verifier: ${this.verification.caveat}`);
    }
    ((this.dossier && this.dossier.researchGaps) || []).forEach((g: any) =>
      out.push(`Research gap: ${typeof g === 'string' ? g : g.detail || JSON.stringify(g)}`),
    );
    return out;
  }

  /** EXPLICIT live/paid run: enrichment + brief, then re-fetch via GET. */
  generatePackBrief(): void {
    if (!this.reg || this.generating) {
      return;
    }
    this.generating = 'packbrief';
    this.genMessage = 'Running live enrichment + brief (paid)…';
    this.svc.generatePackAndBrief(this.reg).subscribe((r) => {
      this.genMessage = r?.error ? `Failed: ${r.detail}` : 'Enrichment + brief generated and persisted.';
      this.reload();
    });
  }

  /** EXPLICIT live/paid run: writer -> verify -> persist, then re-fetch via GET. */
  generateDraft(): void {
    if (!this.reg || this.generating) {
      return;
    }
    this.generating = 'draft';
    this.genMessage = 'Running live writer + verifier (paid)…';
    this.svc.generateDraft(this.reg).subscribe((r) => {
      if (r?.error) {
        this.genMessage = `Failed: ${r.detail}`;
      } else if (r?.persisted === false) {
        this.genMessage = `Not persisted (fail-clean): ${r.detail || r.status}`;
      } else {
        this.genMessage = 'Draft + verification generated and persisted.';
      }
      this.reload();
    });
  }

  /** PRIMARY: natural-language inject. The editor writes a note (+ optional URL); ONE
   * LLM 'interpret' call routes it to exactly one of three handlers, then the UNCHANGED
   * deterministic rules decide. The note NEVER becomes a claim under any route. */
  injectGuided(): void {
    const note = (this.injInstruction || '').trim();
    if (!note || this.injecting) {
      return;
    }
    this.injecting = true;
    this.injMessage = 'Reading your note and deciding how to handle it…';
    this.lastRoute = '';
    this.justPromoted = [];
    this.svc.injectSourceNL(this.reg, note, this.injUrl || null, this.injConfidence).subscribe((r) => {
      this.injecting = false;
      if (r?.error) {
        this.injMessage = (r.status === 422 ? 'Rejected (nothing persisted): ' : 'Failed: ') + (r.detail || '');
        return;
      }
      this.lastRoute = r.route || '';
      this.justPromoted = r.promoted && r.matched_claim ? [r.matched_claim] : [];
      this.injMessage = r.message || '';
      if (r.persisted) {
        this.injInstruction = '';
        this.injUrl = '';
        this.svc.loadAll(this.reg).subscribe((st) => this.applyState(st));
      }
    });
  }

  /** Human-readable label for the route the interpreter chose. */
  routeLabel(route: string): string {
    switch (route) {
      case 'corroborate': return 'Route 1 · corroborate an existing claim';
      case 'direct_research': return 'Route 2 · directed research';
      case 'note': return 'Route 3 · context note';
      default: return '';
    }
  }

  /** FALLBACK (manual precision): inject a source URL bound to a chosen claim id. */
  injectSource(): void {
    const url = (this.injUrl || '').trim();
    if (!url || this.injecting) {
      return;
    }
    this.injecting = true;
    this.injMessage = 'Fetching + reconciling the source (live)…';
    this.justPromoted = [];
    this.justInjectedId = '';
    this.svc
      .injectSource(this.reg, url, this.injSupports || null, this.injConfidence)
      .subscribe((r) => {
        this.injecting = false;
        if (r?.error) {
          // 422 = HARD RULE 1 (unfetchable) — rejected, nothing persisted
          this.injMessage =
            (r.status === 422 ? 'Rejected (nothing persisted): ' : 'Failed: ') + (r.detail || '');
          return;
        }
        const s = r.summary || {};
        this.justPromoted = (s.promotions || []).map((p: any) => p.claim_id);
        this.justInjectedId = s.injected_evidence_id || '';
        this.injMessage =
          `Injected → pack v${r.new_version}. ` +
          (this.justPromoted.length
            ? `Promoted to inferred: ${this.justPromoted.join(', ')}.`
            : 'No claim promoted (independent corroboration not found / same-domain).');
        this.injUrl = '';
        this.svc.loadAll(this.reg).subscribe((st) => this.applyState(st)); // re-fetch (GET)
      });
  }

  // ---- view helpers ---------------------------------------------------------- #

  get pack(): any { return this.state?.enrichedPack?.payload || null; }
  /** Current persisted pack version (post-injection indicator). */
  get packVersion(): number | null { return this.state?.enrichedPack?.version ?? null; }
  isInjected(e: any): boolean { return e?.origin === 'human_injected'; }
  justPromotedNow(e: any): boolean { return this.justPromoted.indexOf(e?.id) >= 0; }
  get brief(): any { return this.state?.brief?.payload || null; }
  get draft(): any { return this.state?.draft?.payload || null; }
  get verification(): any { return this.state?.verification?.payload || null; }
  get dossier(): any { return this.state?.dossier || null; }

  meta(row: ArtifactRow | null | undefined): string {
    if (!row) { return ''; }
    return `v${row.version} · id ${row.id} · run ${(row.pipeline_run_id || '').slice(0, 8)}` +
      (row.model ? ` · ${row.model}` : ' · deterministic') +
      (row.created_by ? ` · ${row.created_by}` : '');
  }

  /** The image URL to try for a media candidate's thumbnail, or null if none is
   * usable (no image URL, or a synthetic placeholder:// url). source_url is the
   * article/page — never the image — so it is deliberately NOT used here. */
  mediaThumb(m: any): string | null {
    const u = (m && (m.thumbnail_url || m.image_url || m.embed_url)) || '';
    if (!u || /^placeholder:\/\//i.test(u)) {
      return null;
    }
    return u;
  }

  /** An image that failed to load (broken / hotlink-blocked) → show the placeholder. */
  onMediaImgError(id: string): void {
    this.brokenImg.add(id);
  }

  // ---- Add media — paste a link OR upload/paste an image, with attribution ----- #

  private setMediaFile(file: File | null): void {
    if (this.addPreview) {
      URL.revokeObjectURL(this.addPreview);
      this.addPreview = '';
    }
    this.addFile = file;
    if (file) {
      this.addPreview = URL.createObjectURL(file);
      this.addUrl = ''; // a chosen file takes precedence over a pasted URL
      this.addType = file.type.startsWith('video') ? 'video' : 'image';
    }
  }

  onMediaFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setMediaFile(input.files && input.files.length ? input.files[0] : null);
  }

  /** Paste an image straight from the clipboard into the add-media panel. */
  onMediaPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) {
      return;
    }
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const f = items[i].getAsFile();
        if (f) {
          event.preventDefault();
          this.setMediaFile(f);
          this.addMsg = 'Image pasted — add a credit, then Add media.';
        }
        return;
      }
    }
  }

  clearAddMedia(): void {
    this.setMediaFile(null);
    this.addUrl = '';
    this.addCaption = '';
    this.addCredit = '';
    this.addCreditUrl = '';
    this.addType = 'image';
  }

  /** Insert the media: upload the file if there is one (→ hosted URL), else use the
   * pasted URL, then add it to the pack as a cleared, attributed media candidate. */
  async addMedia(): Promise<void> {
    if (this.addingMedia) {
      return;
    }
    let url = (this.addUrl || '').trim();
    if (!url && !this.addFile) {
      this.addMsg = 'Paste an image URL, or choose/paste an image file.';
      return;
    }
    this.addingMedia = true;
    this.addMsg = this.addFile ? 'Uploading image…' : 'Adding media…';
    try {
      if (this.addFile) {
        const up: any = await firstValueFrom(this.admin.uploadStaticImage(this.addFile));
        if (!up?.url) {
          this.addingMedia = false;
          this.addMsg = 'Image upload failed.';
          return;
        }
        url = up.url;
      }
      const r: any = await firstValueFrom(
        this.svc.addMedia(this.reg, {
          image_url: url,
          media_type: this.addType,
          caption: this.addCaption,
          attribution: this.addCredit,
          attribution_url: this.addCreditUrl,
        }),
      );
      this.addingMedia = false;
      if (r?.error) {
        this.addMsg = (r.status === 424 ? 'Research the plate first, then add media.' : 'Failed: ') + (r.detail || '');
        return;
      }
      this.addMsg = `Added as cleared media (pack v${r.new_version}).`;
      this.clearAddMedia();
      this.svc.loadAll(this.reg).subscribe((s) => this.applyState(s));
    } catch (e: any) {
      this.addingMedia = false;
      this.addMsg = 'Failed: ' + (e?.message || e);
    }
  }

  /** Canonical rights badge class. */
  rightsClass(usage: string): string {
    switch (usage) {
      case 'cleared': return 'ok';
      case 'editorial_only': return 'info';
      case 'needs_permission': return 'warn';
      case 'blocked': return 'bad';
      default: return 'muted';
    }
  }

  statusClass(status: string): string {
    switch (status) {
      case 'verified': return 'ok';
      case 'inferred': return 'info';
      case 'unverified': return 'warn';
      case 'disputed': return 'bad';
      default: return 'muted';
    }
  }
}
